import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getPromoCodeResponse,
  type InvalidPromoCode,
} from '@hungryroot/app-mhr';
import { PricingNudge } from '@hungryroot/components-order-summary';
import { Price } from '@hungryroot/pho';
import { invalidateCustomerCheckoutPricingInfo } from '@hungryroot/plan';
import { PromoCode } from '@hungryroot/promos';
import { Heading, Paragraph } from '@hungryroot/ramen';
import { PromoType } from '@hungryroot/rest/api/get_pricing_config/get';
import { submitPromo } from '../../frontend/react/src/api/checkout';
import styles from './PromoLineItem.module.css';

interface PromoLineItemProps {
  testid?: string;
  label: string;
  value: number;
  code: string;
  type: PromoType;
  subtotal: number;
  orderId?: number;
}

interface PromoCodeContainerProps {
  label?: string;
  code?: string;
  orderId?: number;
  testid?: string;
  subtotal: number;
  showPricingNudge?: boolean;
}

const getPromoCodeError = async (value: string, subtotal: number) => {
  const response = await getPromoCodeResponse(value);
  if (!('valid' in response)) {
    const threshold = Number(response?.factory_effect?.promos[0].threshold);
    if (threshold > subtotal) {
      return {
        valid: false,
        message: `Promo can only be applied on plans $${threshold} or more`,
      } as InvalidPromoCode;
    }
  }

  return response;
};

export const PromoCodeContainer = (props: PromoCodeContainerProps) => {
  const { label, code, orderId, testid, subtotal, showPricingNudge } = props;
  const queryClient = useQueryClient();
  const promoCodeHasEffect = Boolean(!label);
  const [isPromoEmpty, setIsPromoEmpty] = useState(promoCodeHasEffect);

  const submitPromoWithData = async (code: string) => {
    await submitPromo(orderId, code);
    invalidateCustomerCheckoutPricingInfo(queryClient);
  };

  if (!orderId) return null;

  if (isPromoEmpty) {
    return (
      <div className={styles['promo-code-button-container']}>
        <Heading className={styles['promo-code-button']} variant="H6">
          <button
            data-testid={`${testid}-heading`}
            onClick={() => {
              setIsPromoEmpty(false);
            }}
            className={styles['promo-code-button-button']}
          >
            Enter Promo Code
          </button>
        </Heading>
        {showPricingNudge && (
          <PricingNudge>Spend $99 for 30% off your first delivery</PricingNudge>
        )}
      </div>
    );
  }

  return (
    <PromoCode
      onBlur={
        promoCodeHasEffect
          ? () => {
              setIsPromoEmpty(true);
            }
          : undefined
      }
      initialShowInput={promoCodeHasEffect}
      onSubmit={submitPromoWithData}
      validate={(code) => getPromoCodeError(code, subtotal)}
      effectStr={label || ''}
      code={code}
      testid={testid}
    />
  );
};

export function PromoLineItem(props: PromoLineItemProps) {
  const { testid, label, value, type, code, orderId, subtotal } = props;

  switch (type) {
    case PromoType.FLAT_DISCOUNT:
      return (
        <>
          <PromoCodeContainer
            testid={testid}
            orderId={orderId}
            label={label}
            code={code}
            subtotal={subtotal}
          />
          <Paragraph className={styles.discount}>
            {/* negative for discounts */}
            <Price testid={testid} amount={value * -1} />
          </Paragraph>
        </>
      );
    case PromoType.FREE_SHIPPING:
    // threshold
    case PromoType.FREE_ITEM:
    case PromoType.FREE_ITEM_FOR_LIFE:
      return (
        <>
          <Paragraph className={styles['line-item-label']}>{label}</Paragraph>
          <Paragraph className={styles.free}>Free</Paragraph>
        </>
      );

    case PromoType.REFERRAL_DISCOUNT:
      return (
        <>
          <PromoCodeContainer
            testid={testid}
            orderId={orderId}
            label={label}
            code={code}
            subtotal={subtotal}
          />
          <Paragraph className={styles.discount}>
            {/* negative for discounts */}
            <Price testid={testid} amount={value * -1} />
          </Paragraph>
        </>
      );
    case PromoType.PERCENT_DISCOUNT:
      return (
        <>
          <PromoCodeContainer
            testid={testid}
            orderId={orderId}
            label={label}
            code={code}
            subtotal={subtotal}
          />
          <Paragraph className={styles.discount}>
            {/* negative for discounts */}
            <Price testid={testid} amount={value * -1} />
          </Paragraph>
        </>
      );
  }
}
